import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { adminVerify, adminListSignups } from "../lib/api";

const STORAGE_KEY = "procurmo_admin_token";
const HTTP_UNAUTHORIZED = 401;

/**
 * Encapsulates admin session state (token, auth status) and waitlist data
 * loading. Keeping this logic in a hook keeps the Admin page component small
 * and gives every callback a correct, explicit dependency array.
 *
 * Dependency note: React state setters (setToken, setAuthed, ...), module-level
 * constants (STORAGE_KEY, HTTP_UNAUTHORIZED), stable module imports (adminVerify,
 * adminListSignups, toast) and the `sessionStorage` global are stable and are
 * intentionally NOT listed in dependency arrays, per the React hooks guidelines.
 */
export function useAdminAuth() {
  const [token, setToken] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const clearSession = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setToken("");
    setAuthed(false);
    setItems([]);
    setCount(0);
  }, []);

  const loadData = useCallback(async (tk) => {
    setLoading(true);
    try {
      const data = await adminListSignups(tk);
      setItems(data.items || []);
      setCount(data.count || 0);
    } catch (e) {
      if (e?.response?.status === HTTP_UNAUTHORIZED) {
        toast.error("Sitzung abgelaufen. Bitte erneut anmelden.");
        clearSession();
      } else {
        toast.error("Daten konnten nicht geladen werden.");
      }
    } finally {
      setLoading(false);
    }
  }, [clearSession]);

  const login = useCallback(async (password) => {
    const pw = (password || "").trim();
    if (!pw) return false;
    try {
      const ok = await adminVerify(pw);
      if (!ok) return false;
      sessionStorage.setItem(STORAGE_KEY, pw);
      setToken(pw);
      setAuthed(true);
      toast.success("Willkommen zurück.");
      loadData(pw);
      return true;
    } catch (e) {
      if (e?.response?.status === HTTP_UNAUTHORIZED) toast.error("Falsches Passwort.");
      else toast.error("Anmeldung fehlgeschlagen. Bitte erneut versuchen.");
      return false;
    }
  }, [loadData]);

  const logout = useCallback(() => clearSession(), [clearSession]);
  const refresh = useCallback(() => loadData(token), [loadData, token]);

  // Validate any stored token exactly once on mount. `token` is read from the
  // initial state on purpose; loadData/clearSession are stable callbacks.
  useEffect(() => {
    let active = true;
    (async () => {
      if (!token) {
        setChecking(false);
        return;
      }
      try {
        const ok = await adminVerify(token);
        if (!active) return;
        if (ok) {
          setAuthed(true);
          loadData(token);
        } else {
          clearSession();
        }
      } catch {
        if (active) clearSession();
      } finally {
        if (active) setChecking(false);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { token, authed, checking, items, count, loading, login, logout, refresh };
}
